import { Button, makeStyles, Menu, Theme } from '@material-ui/core';
import { useDropdown } from '@moderepo/mode-app-components';
import { Entity } from '@moderepo/mode-internal-apis';
import { UsageMode } from '@moderepo/mode-ui-state';
import clsx from 'clsx';
import React, { useState, useCallback, CSSProperties } from 'react';


import { EntitiesListingContainer, EntitiesListingContainerProps } from '../../containers';


const useStyle = makeStyles((theme: Theme) => {
    return {

        menu: {
            width                       : '100%',
            minHeight                   : 300,
            height                      : 500,
            maxHeight                   : '100%',
            [theme.breakpoints.up('md')]: {
                width: 600,
            },
            padding: 0,
            '& ul' : {
                width  : '100%',
                height : '100%',
                padding: 0,
            },
        },

        entitiesListWrapper: {
            width        : '100%',
            height       : '100%',
            padding      : 0,
            display      : 'flex',
            flexDirection: 'column',

            '& .actions-bar': {
                padding    : theme.spacing(1),
                textAlign  : 'right',
                '& .action': {
                    marginLeft: theme.spacing(1),
                },
            },
        },

        entitiesListContainer: {
            height  : 0,
            flex    : 1,
            width   : '100%',
            overflow: 'auto',
        },
    };
}, {
    name: 'SelectEntityComp', index: 1,
});



export interface SelectEntityDropdownMenuCompProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly entitiesListingContainerProps: EntitiesListingContainerProps,
    readonly texts: {
        readonly clearButtonLabel: string;
        readonly cancelButtonLabel: string;
        readonly selectButtonLabel: string;
        readonly emptyValueDisplay?: string;           // OPTIONAL value to display if an entity is not selected. Default will be empty string.
    }
    readonly togglerComponent: React.ReactNode;
    readonly onEntitySelected: (entity: Entity)=> void;
}


/**
 * This is a component for selecting an Entity.
 */
export const SelectEntityDropdownMenuComp: React.FC<SelectEntityDropdownMenuCompProps> = (props: SelectEntityDropdownMenuCompProps) => {

    const [entitiesMenuAnchorEl, isEntitiesMenuOpened, onEntitiesMenuAnchorElClicked, onEntitiesMenuClosed] = useDropdown();
    const [selectedEntity, setSelectedEntity] = useState<Entity | undefined>();
    const classes = useStyle();

    const onCancel = useCallback(() => {
        setSelectedEntity(undefined);
        onEntitiesMenuClosed();
    }, [onEntitiesMenuClosed]);



    const onSelectEntity = useCallback(() => {
        if (selectedEntity) {
            props.onEntitySelected(selectedEntity);
            setSelectedEntity(undefined);
            onEntitiesMenuClosed();
        }
    }, [onEntitiesMenuClosed, props, selectedEntity]);


    return (
        <>
            <div
                className={props.className}
                style={props.style}
                onClick={(event) => {
                    onEntitiesMenuAnchorElClicked(event);
                }}
            >
                {props.togglerComponent}
            </div>

            <Menu
                getContentAnchorEl={null}
                anchorEl={entitiesMenuAnchorEl}
                anchorOrigin={{
                    vertical  : 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical  : 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    className: classes.menu,
                }}
                keepMounted
                open={isEntitiesMenuOpened}
                onClose={onEntitiesMenuClosed}
            >
                <span className={classes.entitiesListWrapper}>
                    <EntitiesListingContainer
                        {...props.entitiesListingContainerProps}
                        className={clsx(classes.entitiesListContainer)}
                        canCreateEntities={false}
                        disablePreviewOption
                        minimalUI
                        usageMode={UsageMode.MANAGE_DATA}
                        onEntitySelected={(entity: Entity) => {
                            setSelectedEntity(entity);
                        }}
                    />
                    <div className="actions-bar">
                        <Button
                            className="action"
                            size="small"
                            onClick={onCancel}
                        >
                            {props.texts.cancelButtonLabel}
                        </Button>
                        <Button
                            className="action"
                            variant="contained"
                            color="primary"
                            size="small"
                            disabled={!selectedEntity}
                            onClick={onSelectEntity}
                        >
                            {props.texts.selectButtonLabel}
                        </Button>
                    </div>
                </span>
            </Menu>
        </>
    );
};
